import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Clock, Check, Trash } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RemindersPage = () => {
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCompleted, setShowCompleted] = useState(false);

  useEffect(() => {
    fetchReminders();
  }, [showCompleted]);

  const fetchReminders = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/reminders?include_completed=${showCompleted}`);
      setReminders(data);
    } catch (e) {
      toast.error('Failed to fetch reminders');
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = async (reminderId) => {
    try {
      await axios.put(`${API_URL}/api/reminders/${reminderId}`, { is_completed: true });
      toast.success('Reminder completed');
      fetchReminders();
    } catch (e) {
      toast.error('Failed to update reminder');
    }
  };

  const handleDelete = async (reminderId) => {
    if (!window.confirm('Delete this reminder?')) return;
    try {
      await axios.delete(`${API_URL}/api/reminders/${reminderId}`);
      toast.success('Reminder deleted');
      fetchReminders();
    } catch (e) {
      toast.error('Failed to delete reminder');
    }
  };

  const isOverdue = (dueDate) => {
    return new Date(dueDate) < new Date();
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-32 skeleton"></div>
        <div className="space-y-4">
          {[1, 2, 3].map(i => <div key={i} className="h-24 skeleton rounded-md"></div>)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Reminders</h1>
          <p className="text-muted-foreground">Your follow-ups and reminders</p>
        </div>
        <Button
          variant="outline"
          onClick={() => setShowCompleted(!showCompleted)}
          data-testid="toggle-completed"
        >
          {showCompleted ? 'Hide Completed' : 'Show Completed'}
        </Button>
      </div>

      {reminders.length > 0 ? (
        <div className="space-y-3">
          {reminders.map(reminder => (
            <Card 
              key={reminder.id} 
              className={`card-hover ${reminder.is_completed ? 'opacity-60' : ''}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    reminder.is_completed 
                      ? 'bg-green-100 text-green-600' 
                      : isOverdue(reminder.due_date) 
                        ? 'bg-red-100 text-red-600'
                        : 'bg-primary/10 text-primary'
                  }`}>
                    <Clock size={20} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className={`font-medium ${reminder.is_completed ? 'line-through' : ''}`}>
                        {reminder.title}
                      </h3>
                      {reminder.is_completed && <Badge variant="secondary">Completed</Badge>}
                      {!reminder.is_completed && isOverdue(reminder.due_date) && (
                        <Badge variant="destructive">Overdue</Badge>
                      )}
                    </div>
                    {reminder.description && (
                      <p className="text-sm text-muted-foreground mb-2">{reminder.description}</p>
                    )}
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <span>Due: {new Date(reminder.due_date).toLocaleDateString()}</span>
                      <Link to={`/records/${reminder.record_id}`} className="text-primary hover:underline">
                        View Record
                      </Link>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    {!reminder.is_completed && (
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => handleComplete(reminder.id)}
                        title="Mark as completed"
                        data-testid={`complete-${reminder.id}`}
                      >
                        <Check size={16} />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(reminder.id)}
                      className="text-destructive"
                      data-testid={`delete-${reminder.id}`}
                    >
                      <Trash size={16} />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="empty-state py-12">
            <Clock size={48} className="mb-4 opacity-50" />
            <p className="text-lg font-medium">No reminders</p>
            <p className="text-sm">Create reminders from record details</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default RemindersPage;
